import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import SkeletonLoader from '../common/SkeletonLoader';
import BravoAI from '../BravoAI';
import ChatInterno from '../chat/ChatInterno';
import chatInternoService from '../../services/chatInternoService';
import api from '../../services/api';
import { useDepartamento } from '../../contexts/DepartamentoContext';
import { useUsuario } from '../../contexts/UsuarioContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useAuth } from '../../contexts/AuthContext';

interface LayoutProps {
  children?: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { carregando: carregandoDepartamentos } = useDepartamento();
  const { carregando: carregandoUsuarios } = useUsuario();
  const { menuAberto, fecharMenu } = useSidebar();
  const { usuario } = useAuth();
  const location = useLocation();

  const mainRef = useRef<HTMLElement>(null);

  // useLayoutEffect (não useEffect) para rodar ANTES do navegador pintar a
  // tela — evita um piscar breve mostrando a rolagem antiga da página
  // anterior antes de saltar pro topo.
  useLayoutEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const [chatAberto, setChatAberto] = useState(false);
  const [chatPos, setChatPos] = useState<{ x: number; y: number } | null>(null);
  const [chatNaoLidas, setChatNaoLidas] = useState(0);

  const isAdmin = usuario?.tipo === 'admin';
  const isNaBravoAI = location.pathname === '/bravo-ai';
  const isNoChat = location.pathname === '/chat';

  // Heartbeat global — mantém o usuário como online enquanto o Hub estiver aberto
  useEffect(() => {
    if (!usuario) return;
    const enviar = () => api.post('/chat-interno/heartbeat').catch(() => {});
    enviar();
    const interval = setInterval(enviar, 30000);
    return () => clearInterval(interval);
  }, [usuario]);
  const chatDragRef = useRef({ dragging: false, moved: false, startX: 0, startY: 0, startBtnX: 0, startBtnY: 0 });
  const BTN_W = 140;
  const BTN_H = 48;

  const handleChatDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const defaultBottom = isNaBravoAI ? 24 : 88;
    const currentX = chatPos?.x ?? (window.innerWidth - BTN_W - 24);
    const currentY = chatPos?.y ?? (window.innerHeight - BTN_H - defaultBottom);
    chatDragRef.current = { dragging: true, moved: false, startX: e.clientX, startY: e.clientY, startBtnX: currentX, startBtnY: currentY };

    const onMove = (ev: MouseEvent) => {
      const d = chatDragRef.current;
      if (!d.dragging) return;
      const dx = ev.clientX - d.startX;
      const dy = ev.clientY - d.startY;
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) d.moved = true;
      document.body.style.cursor = 'grabbing';
      setChatPos({
        x: Math.max(8, Math.min(window.innerWidth - BTN_W - 8, d.startBtnX + dx)),
        y: Math.max(8, Math.min(window.innerHeight - BTN_H - 8, d.startBtnY + dy)),
      });
    };

    const onUp = () => {
      document.body.style.cursor = '';
      if (!chatDragRef.current.moved) setChatAberto(prev => !prev);
      chatDragRef.current.dragging = false;
      chatDragRef.current.moved = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [chatPos, isNaBravoAI]);

  // Som de notificação (Web Audio API)
  const tocarSom = () => {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch {}
  };

  // Polling de mensagens não lidas — sempre atualiza com valor real do servidor
  useEffect(() => {
    if (!usuario) return;
    const buscarNaoLidas = () => {
      chatInternoService.totalNaoLidas()
        .then(total => {
          setChatNaoLidas(prev => {
            // Toca som apenas se aumentou E o chat está fechado
            if (total > prev && !chatAberto) tocarSom();
            return total;
          });
        })
        .catch(() => {});
    };
    buscarNaoLidas();
    const interval = setInterval(buscarNaoLidas, 5000);
    return () => clearInterval(interval);
  }, [usuario, chatAberto]);

  const carregandoInicial = carregandoDepartamentos || carregandoUsuarios;

  return (
    <div className="flex h-screen overflow-hidden bg-bravo-tertiary dark:bg-gray-900">
      {/* Overlay para fechar o menu quando clicado (visível apenas em mobile quando o menu está aberto) */}
      {menuAberto && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={fecharMenu}
          aria-label="Fechar menu"
        />
      )}

      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <Header />

        <main ref={mainRef} className="flex-1 overflow-y-auto p-6" style={{ overflowAnchor: 'none' }}>
          <div className="container mx-auto">
            {/* Skeleton só na área de conteúdo — Sidebar e Header continuam
                visíveis e normais, em vez de escurecer a tela toda. */}
            {carregandoInicial ? <SkeletonLoader /> : (children || <Outlet />)}
          </div>
        </main>
      </div>
      {!isNaBravoAI && <BravoAI />}

      {/* Botão flutuante do Chat */}
      {!isNoChat && (
        <>
          <button
            onMouseDown={handleChatDragStart}
            title="Conversas — arraste para mover"
            style={{
              position: 'fixed',
              ...(chatPos ? { left: chatPos.x, top: chatPos.y } : { bottom: isNaBravoAI ? 24 : 88, right: 24 }),
              height: 48,
              paddingLeft: 16,
              paddingRight: 16,
              borderRadius: 24,
              background: 'linear-gradient(135deg, #1a1a2e 0%, #2c2c54 100%)',
              border: 'none',
              boxShadow: '0 4px 16px rgba(26,26,46,0.45)',
              cursor: 'grab',
              zIndex: 10001,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              userSelect: 'none',
              transition: 'box-shadow 0.2s',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 20px rgba(26,26,46,0.65)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 16px rgba(26,26,46,0.45)'; }}
          >
            {chatAberto ? (
              <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="#fff">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H5.17L4 17.17V4H20V16Z"/>
                </svg>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: 13, letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
                  Conversas
                </span>
                {chatNaoLidas > 0 && (
                  <span style={{
                    position: 'fixed',
                    ...(chatPos
                      ? { top: chatPos.y - 12, left: chatPos.x + BTN_W / 2 - 10 }
                      : { bottom: isNaBravoAI ? 80 : 144, right: 34 }
                    ),
                    background: '#e74c3c', color: '#fff',
                    borderRadius: '50%', minWidth: 22, height: 22,
                    fontSize: 11, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                    zIndex: 10001, padding: '0 4px',
                  }}>
                    {chatNaoLidas > 99 ? '99+' : chatNaoLidas}
                  </span>
                )}
              </>
            )}
          </button>
          {chatAberto && <ChatInterno onFechar={() => setChatAberto(false)} onNaoLidasChange={setChatNaoLidas} />}
        </>
      )}
    </div>
  );
};

export default Layout;
